import { createReducer, on } from '@ngrx/store';
import moment from 'moment';

import { vddToHide } from '../remote-diagnostics';

import { RemoteDiagnosticsSnapshotsActions } from './remote-diagnostics-snapshots.actions';
import { SnapshotType } from './remote-diagnostics-snapshots.model';
import { RemoteDiagnosticsSnapshotsState } from './remote-diagnostics-snapshots.state';

export const initRemoteDiagnosticsSnapshotsState: RemoteDiagnosticsSnapshotsState = {
  // fetch snapshots
  snapshots: [],
  fetchSnapshotsLoading: false,
  fetchSnapshotsSuccess: false,
  fetchSnapshotsError: null,
  snapshotType: SnapshotType.BACKUP,
  selectedControlUnit: null,
  vddListForSelectedControlUnit: [],
  selectedParametersForNewSnapshot: [],

  // fetch installation vdd list
  installationVDDList: [],
  fetchInstallationVDDListLoading: false,
  fetchInstallationVDDListSuccess: false,
  fetchInstallationVDDListError: null,

  // create snapshot
  createSnapshotLoading: false,
  createSnapshotSuccess: false,
  createSnapshotError: null,

  // fetch vdds from snapshot
  fetchSnapshotDetailsLoading: false,
  fetchSnapshotDetailsSuccess: false,
  fetchSnapshotDetailsError: null,
  snapshotDetails: [],
  selectedSnapshot: null,

  // read vdds from snapshot
  finishedReadingVddsAtDate: null,

  // optimization flag
  showValues: false,
  isShowingMoreSnapshots: false,
  filterDate: moment(),
  lastsNumberOfSnapshots: null,
};

export const remoteDiagnosticsSnapshotsReducer = createReducer(
  initRemoteDiagnosticsSnapshotsState,

  on(
    RemoteDiagnosticsSnapshotsActions.fetchSnapshots,
    (state, { snapshotType, wasBackupGenerated }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      snapshots: [],
      fetchSnapshotsLoading: true,
      fetchSnapshotsSuccess: false,
      fetchSnapshotsError: null,
      snapshotType,
      createSnapshotLoading: false,
      createSnapshotSuccess: wasBackupGenerated,
      createSnapshotError: null,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.fetchSnapshotsSuccess,
    (state, { snapshots }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchSnapshotsLoading: false,
      fetchSnapshotsSuccess: true,
      snapshots,
      fetchSnapshotsError: null,
      lastsNumberOfSnapshots: snapshots.length,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.fetchSnapshotsFailed,
    (state, { error }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchSnapshotsLoading: false,
      fetchSnapshotsSuccess: false,
      snapshots: [],
      fetchSnapshotsError: error,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.fetchSnapshotDetails,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchSnapshotDetailsLoading: true,
      fetchSnapshotDetailsSuccess: false,
      fetchSnapshotDetailsError: null,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.fetchSnapshotDetailsSuccess,
    (state, { snapshotDetails }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchSnapshotDetailsLoading: false,
      fetchSnapshotDetailsSuccess: true,
      fetchSnapshotDetailsError: null,
      snapshotDetails,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.fetchSnapshotDetailsFailed,
    (state, { error }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchSnapshotDetailsLoading: false,
      fetchSnapshotDetailsSuccess: false,
      fetchSnapshotDetailsError: error,
      snapshotDetails: null,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.setInstallationVDDList,
    (state, { vddList }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      installationVDDList: vddList.filter((vdd) => vdd.key !== vddToHide),
    })
  ),
  on(
    RemoteDiagnosticsSnapshotsActions.loadVddListSuccess,
    (state, { parameters }): RemoteDiagnosticsSnapshotsState => {
      return {
        ...state,
        vddListForSelectedControlUnit: parameters,
      };
    }
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.addParameter,
    (state, { vdd }): RemoteDiagnosticsSnapshotsState => {
      return {
        ...state,
        selectedParametersForNewSnapshot: state.selectedParametersForNewSnapshot.concat(vdd),
      };
    }
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.deleteParameter,
    (state, { vdd }): RemoteDiagnosticsSnapshotsState => {
      return {
        ...state,

        selectedParametersForNewSnapshot: state.selectedParametersForNewSnapshot.filter(
          (selectedParameter) =>
            !(
              selectedParameter.key === vdd.key && selectedParameter.logicalLink === vdd.logicalLink
            )
        ),
      };
    }
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.deleteAllParameters,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      selectedParametersForNewSnapshot: [],
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.selectControlUnit,
    (state, { controlUnit }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      selectedControlUnit: controlUnit,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.fetchInstallationVDDList,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchInstallationVDDListLoading: true,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.fetchInstallationVDDListSuccess,
    (state, { vddList }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchInstallationVDDListLoading: false,
      installationVDDList: vddList.filter((vdd) => vdd.key !== vddToHide),
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.fetchInstallationVDDListFailed,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchInstallationVDDListLoading: false,
      installationVDDList: [],
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.createSnapshotOfTypeBackup,
    RemoteDiagnosticsSnapshotsActions.createSnapshotOfTypeSnapshot,
    RemoteDiagnosticsSnapshotsActions.updateSnapshotWithVddContent,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      createSnapshotLoading: true,
      createSnapshotSuccess: false,
      createSnapshotError: null,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.createSnapshotFailed,
    (state, { error }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      createSnapshotLoading: false,
      createSnapshotSuccess: false,
      createSnapshotError: error,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.updateSnapshotWithVddContentFailed,
    (state, { error }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      createSnapshotLoading: false,
      createSnapshotSuccess: false,
      createSnapshotError: error,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.cancelSnapshotVddSuccess,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchSnapshotsLoading: true,
      fetchSnapshotsSuccess: false,
      fetchSnapshotsError: null,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.cancelSnapshotVddFailed,
    (state, { error }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      createSnapshotLoading: false,
      createSnapshotSuccess: false,
      createSnapshotError: error,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.updateSnapshotWithVddContentSuccess,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      createSnapshotLoading: false,
      createSnapshotSuccess: true,
      createSnapshotError: null,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.snapshotSelected,
    (state, { snapshot }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      selectedSnapshot: snapshot,
      snapshotDetails: !snapshot ? [] : state.snapshotDetails,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.setDateOfTheValuesRead,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      finishedReadingVddsAtDate: new Date(),
      createSnapshotSuccess: false,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.clearDateOfTheValuesRead,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      finishedReadingVddsAtDate: null,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.hideValues,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      showValues: false,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.showValues,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      showValues: true,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.showLessSnapshots,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      isShowingMoreSnapshots: false,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.showMoreSnapshots,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      isShowingMoreSnapshots: true,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.setFilterDate,
    (state, { date }): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      filterDate: date,
    })
  ),

  on(
    RemoteDiagnosticsSnapshotsActions.clearState,
    (state): RemoteDiagnosticsSnapshotsState => ({
      ...state,
      fetchSnapshotDetailsLoading: false,
      snapshotDetails: [],
      selectedSnapshot: null,
    })
  )
);
